import type { TaskBoard } from '@hbac/contract';
import type { HeldDetail } from './task-changes';

/**
 * The task board, remembered by the browser (Plan AK 1): opening Công việc or
 * a task seen recently shows what was there at once, then one cheap "anything
 * new?" read brings it up to date.
 *
 * localStorage, per member, cleared on sign-out and when someone else signs in.
 * Unlike the app data (app-data-cache.ts, sessionStorage), it holds no email or
 * bank detail: task titles, people's names and comments that every current
 * member may read anyway, on a device whose session already stays signed in in
 * localStorage. The live channel's ticket is never kept.
 */

const VERSION = 1;
/** Most recently opened tasks kept with their description and timeline. */
export const DETAILS_KEPT = 30;
const BOARD = 'hbac-tasks:';
const DETAILS = 'hbac-task-details:';

export interface Kept<T> {
  data: T;
  /** When it was saved (epoch ms): shown as already stale, so it is always read again. */
  at: number;
}

/** A board worth keeping: loaded, cards and projects, without the ticket. */
export function packBoard(board: TaskBoard | undefined, now = Date.now()): string | null {
  if (!board?.cards || !board.projects) return null;
  return JSON.stringify({ v: VERSION, at: now, rev: board.rev, cards: board.cards, projects: board.projects });
}

export function unpackBoard(raw: string | null): Kept<TaskBoard> | null {
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as { v?: unknown; at?: unknown; rev?: unknown; cards?: unknown; projects?: unknown };
    if (d.v !== VERSION || typeof d.at !== 'number' || typeof d.rev !== 'number' || !Array.isArray(d.cards) || !Array.isArray(d.projects)) return null;
    return { at: d.at, data: { rev: d.rev, cards: d.cards, projects: d.projects, liveTicket: null } as TaskBoard };
  } catch {
    return null;
  }
}

type StoredDetail = { at: number; detail: HeldDetail };

function unpackDetailList(raw: string | null): StoredDetail[] {
  if (!raw) return [];
  try {
    const d = JSON.parse(raw) as { v?: unknown; items?: unknown };
    if (d.v !== VERSION || !Array.isArray(d.items)) return [];
    return (d.items as StoredDetail[]).filter((i) => typeof i?.at === 'number' && typeof i.detail?.task?.id === 'number' && Array.isArray(i.detail.activity));
  } catch {
    return [];
  }
}

/**
 * The kept list with this task first. Only a task read in full is kept: not the
 * card shown while it loads, not one known to be deleted (that one is dropped).
 */
export function rememberDetail(raw: string | null, detail: HeldDetail, now = Date.now()): string | null {
  const list = unpackDetailList(raw).filter((i) => i.detail.task.id !== detail.task.id);
  if (detail.partial) return null;
  const items = detail.deleted
    ? list
    : [{ at: now, detail: { task: detail.task, activity: detail.activity, rev: detail.rev } }, ...list].slice(0, DETAILS_KEPT);
  return JSON.stringify({ v: VERSION, items });
}

export function findDetail(raw: string | null, taskId: number): Kept<HeldDetail> | null {
  const hit = unpackDetailList(raw).find((i) => i.detail.task.id === taskId);
  return hit ? { at: hit.at, data: hit.detail } : null;
}

// --- Storage: every access guarded, the cache is an optimisation, never required.

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value !== null) localStorage.setItem(key, value);
  } catch {
    // Quota or blocked storage: nothing is remembered, everything still works.
  }
}

export const readKeptBoard = (memberId: string) => unpackBoard(read(BOARD + memberId));
export const keepBoard = (memberId: string, board: TaskBoard | undefined) => write(BOARD + memberId, packBoard(board));
export const readKeptDetail = (memberId: string, taskId: number) => findDetail(read(DETAILS + memberId), taskId);
export const keepDetail = (memberId: string, detail: HeldDetail) =>
  write(DETAILS + memberId, rememberDetail(read(DETAILS + memberId), detail));

/** Forgets the board of everyone but `keep` (sign-out: nobody). */
export function forgetTasks(keep: string | null = null): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k || !(k.startsWith(BOARD) || k.startsWith(DETAILS))) continue;
      if (keep !== null && (k === BOARD + keep || k === DETAILS + keep)) continue;
      localStorage.removeItem(k);
    }
  } catch {
    // Blocked storage: there was nothing kept either.
  }
}
